import Link from "next/link";

// Bottom band height reserved for the footer text, peeking out below the
// content plate — matches the 29px reveal in the source design (304 - 275).
const FOOTER_BAND = 29;

/**
 * Wraps a dropdown's main content in a "nav" plate (bg-grey-900). When a
 * footer label or CTA is provided, it sits on top of a slightly
 * bigger "nav footer" plate (bg-grey-700): peeking out 1px on top/left/right
 * and revealing a band at the bottom where the footer link lives. With
 * neither authored, the footer plate/band is omitted entirely.
 */
export default function MegaMenuFooter({
  children,
  clickSubmenu,
  footerLabel,
  footerCta,
}: {
  children: React.ReactNode;
  clickSubmenu: (link: string, external?: boolean) => void;
  footerLabel?: string;
  footerCta?: { title?: string; link?: string };
}) {
  const hasFooter = Boolean(footerLabel || footerCta?.title);

  if (!hasFooter) {
    return (
      <div className="relative rounded-[10px] bg-grey-900 p-5">
        <div aria-hidden className="absolute -inset-px rounded-[10px] bg-grey-700 -z-10" />
        {children}
      </div>
    );
  }

  const ctaLink = footerCta?.link;
  const bandClass =
    "absolute inset-x-0 bottom-0 flex items-center justify-between px-5 text-[12px] transition-colors";
  const bandContent = (
    <>
      <span className="text-white/70">{footerLabel}</span>
      {footerCta?.title && (
        <span className="text-white font-semibold hover:underline">{footerCta.title}</span>
      )}
    </>
  );

  return (
    <div className="relative" style={{ paddingBottom: FOOTER_BAND }}>
      <div
        aria-hidden
        className="absolute -top-px -left-px -right-px bottom-0 rounded-[10px] bg-grey-700 -z-10"
      />

      <div className="relative rounded-[10px] bg-grey-900 p-5">{children}</div>

      {/* Only a link when a destination is actually authored — a label-only
          footer stays inert rather than becoming an href="#" that scrolls to
          the top of the page and closes the menu. */}
      {ctaLink ? (
        <Link
          href={ctaLink}
          onClick={() => clickSubmenu(ctaLink)}
          className={bandClass}
          style={{ height: FOOTER_BAND }}
        >
          {bandContent}
        </Link>
      ) : (
        <div className={bandClass} style={{ height: FOOTER_BAND }}>
          {bandContent}
        </div>
      )}
    </div>
  );
}
